import {
  Radio as AriaRadio,
  RadioGroup as AriaRadioGroup,
  RadioGroupProps as AriaRadioGroupProps,
  RadioGroupRenderProps,
  RadioProps as AriaRadioProps,
  ValidationResult as AriaValidationResult,
  composeRenderProps,
  Text,
} from 'react-aria-components';

import { cn } from '../../../lib/stylingUtils';
import { FieldError, Label, labelVariants } from '../Field';

const RadioGroup = ({ className, ...props }: AriaRadioGroupProps) => (
  <AriaRadioGroup
    className={composeRenderProps(
      cn('@RadioGroup', className),
      (className, renderProps: RadioGroupRenderProps) =>
        cn(
          'group/radiogroup flex flex-col flex-wrap gap-2',
          renderProps.orientation === 'horizontal' && 'flex-row items-center',
          className,
        ),
    )}
    {...props}
  />
);

const Radio = ({ className, children, ...props }: AriaRadioProps) => (
  <AriaRadio
    className={composeRenderProps(className, (className) =>
      cn(
        '@Radio',
        'group/radio flex items-center gap-x-2 text-sm',
        /* Disabled */
        'rac-disabled:cursor-not-allowed rac-disabled:opacity-70',
        labelVariants,
        className,
      ),
    )}
    {...props}
  >
    {composeRenderProps(children, (children, _renderProps) => (
      <>
        <span
          className={cn(
            'ring-offset-background flex aspect-square size-4 items-center justify-center rounded-full border border-primary',
            /* Focus */
            'group-data-[focus]/radio:outline-none',
            /* Focus Visible */
            'group-data-[focus-visible]/radio:ring-2 group-data-[focus-visible]/radio:ring-primary group-data-[focus-visible]/radio:ring-offset-2',
            /* Disabled */
            'group-data-[disabled]/radio:cursor-not-allowed group-data-[disabled]/radio:opacity-50',
            /* Invalid */
            'group-data-[invalid]/radio:border-failure',
          )}
        >
          <span
            className={cn(
              'size-2.5 rounded-full bg-space transition-colors',
              /* Selected */
              'group-data-[selected]/radio:bg-primary',
              /* Invalid */
              'group-data-[selected]/radio:group-data-[invalid]/radio:bg-failure',
            )}
          />
        </span>
        {children}
      </>
    ))}
  </AriaRadio>
);

interface RadioGroupFieldProps extends AriaRadioGroupProps {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: AriaValidationResult) => string);
}

const RadioGroupField = ({
  label,
  description,
  className,
  errorMessage,
  children,
  ...props
}: RadioGroupFieldProps) => (
  <RadioGroup
    className={composeRenderProps(
      cn('@RadioGroupField', className),
      (className) => cn('group/radiogroup flex-col items-start', className),
    )}
    {...props}
  >
    {composeRenderProps(children, (children) => (
      <>
        <Label>{label}</Label>
        <div className="flex flex-col flex-wrap gap-2 group-data-[orientation=horizontal]/radiogroup:flex-row">
          {children}
        </div>
        {description && (
          <Text slot="description" className="text-sm text-neutral/20">
            {description}
          </Text>
        )}
        <FieldError>{errorMessage}</FieldError>
      </>
    ))}
  </RadioGroup>
);

export { RadioGroup, Radio, RadioGroupField };
export type { RadioGroupFieldProps };
